import { TypedDocumentNode, gql } from '@apollo/client';
import { useRef, useState } from 'react';
import { generatePath } from 'react-router-dom';

import { useTypedParams } from '@sorare/routing';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { SEARCH_PARAMS } from '@sorare/core/src/components/search/InstantSearch/types';
import { buildFilterQuery } from '@sorare/core/src/components/search/InstantSearch/utils';
import {
  FOOTBALL_MY_CLUB_SLUG,
  FOOTBALL_MY_CLUB_SLUG_CARDS,
} from '@sorare/core/src/constants/__generated__/routes';
import { FlagSet } from '@sorare/core/src/contexts/featureFlags';
import { useScreenSize } from '@sorare/core/src/hooks/device/useScreenSize';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';

import { DeckEditorDialog } from '@sorare/marketplace/src/components/decks/DeckEditorDialog';

import { Aside } from '@sorare/gameplay/src/components/profile/Aside';
import { ClubLayout } from '@sorare/gameplay/src/components/profile/ClubLayout';
import {
  Gallery,
  MAX_CARDS_TO_DISPLAY,
} from '@sorare/gameplay/src/components/profile/Gallery';
import { ManagerIDHeader } from '@sorare/gameplay/src/components/profile/ManagerIDHeader';
import {
  CampaignWinner,
  RepresentedBy,
} from '@sorare/gameplay/src/components/profile/RepresentedBy';
import {
  FlexGrowBlock,
  Grid,
  Main,
  NewGrid,
} from '@sorare/gameplay/src/components/profile/ui';

import {
  FootballManagerProfileQuery,
  FootballManagerProfileQueryVariables,
} from './__generated__/page.graphql';
import { PageParams } from './__generated__/routeParams';

const FOOTBALL_MANAGER_PROFILE_QUERY = gql`
  query FootballManagerProfileQuery(
    $user: String!
    $sport: Sport!
    $firstCardsToDisplay: Int!
  ) {
    user(slug: $user) {
      slug
      cards(
        sport: FOOTBALL
        first: $firstCardsToDisplay
        rarities: [unique, super_rare, rare, limited, common]
        inOrderOfRarities: true
      ) {
        nodes {
          slug
          ...Gallery_anyCard
        }
      }
      footballUserProfile {
        id
        clubName
        background {
          id
          pictureUrl
        }
        socialPictureFingerprint

        highlightedDeck {
          slug
          name
          ...Gallery_deck
        }
        ...ManagerIDHeader_userSportProfile
      }
      ...ManagerIDHeader_user
      ...Gallery_publicUserInfoInterface
    }
  }
  ${ManagerIDHeader.fragments.user}
  ${ManagerIDHeader.fragments.userSportProfile}
  ${Gallery.fragments.deck}
  ${Gallery.fragments.anyCard}
  ${Gallery.fragments.publicUserInfoInterface}
` as TypedDocumentNode<
  FootballManagerProfileQuery,
  FootballManagerProfileQueryVariables
>;

const FootballManagerProfilePage = () => {
  const { slug } = useTypedParams<PageParams>();
  const {
    flags: { campaignsWinners = [] },
  }: {
    flags: {
      campaignsWinners?: CampaignWinner[];
    } & FlagSet;
  } = useFeatureFlags();
  const { up: isLaptopAndAbove } = useScreenSize('laptop');
  const galleryScrollRef = useRef<HTMLDivElement>(null);
  const { data, loading } = useQuery(FOOTBALL_MANAGER_PROFILE_QUERY, {
    variables: {
      sport: Sport.FOOTBALL,
      user: slug,
      firstCardsToDisplay: MAX_CARDS_TO_DISPLAY,
    },
    skip: !slug,
  });
  const [editingListSlug, setEditingListSlug] = useState<string | null>();

  if (loading || !data?.user) {
    return <LoadingIndicator fullHeight />;
  }

  const { cards, footballUserProfile } = data.user;

  const shareProps = {
    managerProfilePath: generatePath(FOOTBALL_MY_CLUB_SLUG, { slug }),
    managerProfileSocialPictures:
      data?.user.footballUserProfile?.managerProfileSocialPictureUrls,
    galleryPath: generatePath(FOOTBALL_MY_CLUB_SLUG_CARDS, { slug }),
    gallerySocialPictures:
      data?.user.footballUserProfile?.gallerySocialPictureUrls,
    highlightedDeckPath: `${generatePath(FOOTBALL_MY_CLUB_SLUG_CARDS, {
      slug,
    })}?${buildFilterQuery({
      [SEARCH_PARAMS.DECK]:
        data?.user.footballUserProfile?.highlightedDeck?.name,
    })}`,
    highlightedDeckSocialPictures:
      data?.user.footballUserProfile?.highlightedDeck?.socialPictureUrls,
  };

  const campaignWinner = campaignsWinners.find(
    ({ represents }) => represents === data?.user.id
  );

  const blocks = {
    header: (
      <ManagerIDHeader
        user={data.user}
        sport={Sport.FOOTBALL}
        userSportProfile={footballUserProfile}
        {...shareProps}
      />
    ),
    gallery: (
      <>
        <Gallery
          scrollRef={galleryScrollRef}
          sport={Sport.FOOTBALL}
          cards={cards.nodes}
          highlightedDeck={footballUserProfile?.highlightedDeck}
          onAddDeck={() => setEditingListSlug(null)}
          onEditDeck={deckSlug => setEditingListSlug(deckSlug)}
          user={data.user}
        />
        {editingListSlug !== undefined && (
          <DeckEditorDialog
            sport={Sport.FOOTBALL}
            slug={editingListSlug || ''}
            defaultName="Hall Of Fame"
            onClose={() => setEditingListSlug(undefined)}
          />
        )}
      </>
    ),
    representedBy: campaignWinner && (
      <RepresentedBy campaignWinner={campaignWinner} />
    ),
  };

  const backgroundUrl = footballUserProfile?.background?.pictureUrl;

  if (!isLaptopAndAbove) {
    return (
      <ClubLayout home bannerUrl={backgroundUrl}>
        <Grid>
          {blocks.header}
          {blocks.representedBy}
          {blocks.gallery}
        </Grid>
      </ClubLayout>
    );
  }
  return (
    <ClubLayout home bannerUrl={backgroundUrl}>
      <NewGrid>
        <Main>
          <FlexGrowBlock>{blocks.header}</FlexGrowBlock>
        </Main>
        <Aside>
          <FlexGrowBlock>{blocks.gallery}</FlexGrowBlock>
          {blocks.representedBy}
        </Aside>
      </NewGrid>
    </ClubLayout>
  );
};

export default FootballManagerProfilePage;
